/**
 * Portal v2 domain detail (`/portal/domains/$domainId`).
 *
 * Reached from the domains list or from a sender's per-domain breakdown. Its
 * job is to put one domain's posture in one place and then hand off to the
 * actions that live elsewhere — selecting the domain as the active scope is
 * what makes Policy and Settings apply to it, so the page offers that
 * explicitly rather than leaving the customer to find the topbar switcher.
 */
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, Globe, ShieldCheck } from "lucide-react";
import { Button, Skeleton } from "@ddmarc/ui";
import { portalApi } from "@/lib/portal-api";
import { usePortalScope } from "@/lib/portal-scope-context";
import { CardV2, KpiTile, SectionHeader, StatPill } from "@/components/primitives";

const passRateTone = (pct: number): "good" | "warn" | "danger" =>
  pct >= 95 ? "good" : pct >= 80 ? "warn" : "danger";

export function PortalDomainDetailV2Page() {
  const { domainId } = useParams({ from: "/portal/domains/$domainId" });
  const id = Number(domainId);
  const { selectedDomainId, setSelectedDomainId } = usePortalScope();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["portal", "domain", id],
    queryFn: () => portalApi.getDomain(id),
  });

  const backLink = (
    <Link to="/portal/domains">
      <Button variant="ghost" size="sm" className="gap-1.5 px-2 text-[12.5px]">
        <ArrowLeft className="h-3.5 w-3.5" />
        Domains
      </Button>
    </Link>
  );

  if (isLoading) {
    return (
      <div className="space-y-3.5">
        {backLink}
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-3.5">
        {backLink}
        <CardV2 title="Domain not found">
          <p className="text-[12.5px] text-muted-foreground">
            This domain does not exist, or is not part of your account.
          </p>
        </CardV2>
      </div>
    );
  }

  const isActiveScope = selectedDomainId === data.id;

  return (
    <div className="space-y-3.5">
      {backLink}

      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            <code className="font-mono-data">{data.domain}</code>
          </span>
        }
        subtitle={data.is_verified ? "Verified and monitored" : "Awaiting DNS verification"}
      />

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Pass rate"
          value={
            <StatPill tone={passRateTone(data.pass_rate)}>
              {data.pass_rate.toFixed(1)}%
            </StatPill>
          }
          hint="All reported mail"
        />
        <KpiTile label="Reports" value={data.reports_count.toLocaleString()} hint="Received" />
        <KpiTile
          label="Last report"
          value={data.last_report_at ? format(new Date(data.last_report_at), "MMM d") : "—"}
          hint={data.last_report_at ? format(new Date(data.last_report_at), "yyyy") : "None yet"}
        />
        <KpiTile
          label="Status"
          value={
            <StatPill tone={data.is_verified ? "good" : "warn"}>
              {data.is_verified ? "Verified" : "Pending"}
            </StatPill>
          }
          hint="DNS verification"
        />
      </div>

      <CardV2
        title="Work on this domain"
        subtitle="Policy, DNS checks and notifications apply to the domain selected above."
      >
        <div className="flex flex-wrap items-center gap-2">
          {isActiveScope ? (
            <StatPill tone="good">Currently selected</StatPill>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setSelectedDomainId(data.id)}>
              Select this domain
            </Button>
          )}
          <Link to="/portal/policy">
            <Button size="sm" variant="ghost" className="gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              Policy rollout
            </Button>
          </Link>
          <Link to="/portal/reports">
            <Button size="sm" variant="ghost">
              Reports
            </Button>
          </Link>
        </div>
      </CardV2>
    </div>
  );
}
